import React, { useEffect, useState, useRef, useCallback } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "../styles/Vlogs.css";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
export default function Vlogs() {
    const { loggedIn, fetchWithAuth, user, loading: authLoading } = useAuth();

    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();

    const [vlogs, setVlogs] = useState([]);
    const [profileImages, setProfileImages] = useState({});
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const [commentTexts, setCommentTexts] = useState({});
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [openComments, setOpenComments] = useState({});
    const [likes, setLikes] = useState({});
    const observer = useRef();

    // ---------- Helpers ----------
    const isImage = (url) => /(jpeg|jpg|png|gif|webp)(\?|$)/i.test(url || "");
    const isVideo = (url) => /(mp4|webm|ogg)(\?|$)/i.test(url || "");

    // ---------- Fetch Profile Image ----------
    const fetchProfileImage = async (userEmail) => {
        try {
            const res = await fetch(`${API_URL}/profile-image?userEmail=${encodeURIComponent(userEmail)}`);
            if (!res.ok) throw new Error("Failed to fetch profile image");
            const data = await res.json();
            return data.profileImageUrl || "https://tse1.explicit.bing.net/th/id/OIP.hGSCbXlcOjL_9mmzerqAbQHaHa?r=0&rs=1&pid=ImgDetMain&o=7&rm=3";
        } catch (err) {
            console.error(err);
            return "/default-profile.png";
        }
    };

    // ---------- Fetch Vlogs ----------
    const fetchVlogs = async (query = "", p = 0, append = false) => {
        setLoading(true);
        try {
            const url = query
                ? `${API_URL}/vlogs?search=${encodeURIComponent(query)}&page=${p}&limit=4`
                : `${API_URL}/vlogs?page=${p}&limit=4`;

            const res = await fetchWithAuth(url);
            if (!res.ok) throw new Error("Failed to fetch vlogs");
            const data = await res.json();

            const newVlogs = data.vlogs || [];
            const updatedVlogs = append ? [...vlogs, ...newVlogs] : newVlogs;
            setVlogs(updatedVlogs);
            setHasMore(newVlogs.length === 4);

            // Fetch missing profile images
            const missingEmails = newVlogs
                .map(v => v.userEmail)
                .filter(email => !profileImages[email]);

            let imagesMap = {};
            if (missingEmails.length > 0) {
                const results = await Promise.all(
                    missingEmails.map(e => fetchProfileImage(e))
                );
                imagesMap = missingEmails.reduce((acc, email, i) => {
                    acc[email] = results[i];
                    return acc;
                }, {});
                setProfileImages(prev => ({ ...prev, ...imagesMap }));
            }

            // ✅ Save to cache
            localStorage.setItem("vlogs_cache", JSON.stringify({
                vlogs: updatedVlogs,
                profileImages: { ...profileImages, ...imagesMap },
                page: p,
                hasMore: newVlogs.length === 4
            }));

        } catch (err) {
            console.error(err);
            setMessage({ type: "error", text: "Error fetching vlogs" });
            setTimeout(() => setMessage(null), 4000);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading && !loggedIn) {
            navigate('/login');
            return;
        }
        fetchVlogs("", 0);
    }, []);

    // ---------- Infinite Scroll ----------
    const lastVlogRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
                fetchVlogs(search, page + 1, true);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, search, page]);

    // ---------- Create Vlog ----------
    const handleCreateVlog = async (e) => {
        e.preventDefault();
        if (!file || !title) return alert("Please provide title and file");

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("userEmail", user.email);
            formData.append("userName", user.name);
            formData.append("title", title);
            formData.append("description", description);
            formData.append("tags", tags);
            formData.append("vlog", file);

            const res = await fetch(`${API_URL}/create-vlog`, {
                method: "POST",
                body: formData,
            });

            if (!res.ok) throw new Error("Failed to upload vlog");
            const saved = await res.json(); // ✅ get the saved vlog back from API

            toast.success('🎥 Vlog posted successfully..!');

            // ✅ Add new vlog at the top of state
            setVlogs(prev => [saved, ...prev]);

            // ✅ Update cache
            const cached = JSON.parse(localStorage.getItem("vlogs_cache")) || {};
            const updated = [saved, ...(cached.vlogs || [])];
            localStorage.setItem("vlogs_cache", JSON.stringify({
                ...cached,
                vlogs: updated,
            }));

            // Reset form + close modal
            setTitle(""); setDescription(""); setTags(""); setFile(null); setShowModal(false);

        } catch (err) {
            console.error(err);
            alert("Error uploading vlog");
        } finally {
            setUploading(false);
        }
    };

    // ---------- Like / Unlike ----------
    const toggleLike = async (vlogId) => {
        try {
            await fetch(`${API_URL}/vlogs/${vlogId}/like`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userEmail: user.email, userName: user.name }),
            });
            fetchVlogs(search, 0); // refresh like count
        } catch (err) {
            console.error("Error syncing like", err);
        }
    };

    // ---------- Toggle Comments ----------
    const toggleComments = async (vlogId) => {
        setOpenComments(prev => ({
            ...prev,
            [vlogId]: !prev[vlogId]
        }));

        if (!openComments[vlogId]) {
            try {
                const res = await fetch(`${API_URL}/vlogs/${vlogId}/comments?limit=5`);
                if (!res.ok) throw new Error("Failed to fetch comments");
                const data = await res.json();
                setVlogs(prev => prev.map(v => v._id === vlogId ? { ...v, comments: data.comments } : v));
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleCommentChange = (vlogId, text) => {
        setCommentTexts(prev => ({ ...prev, [vlogId]: text }));
    };

    const handleAddComment = async (vlogId) => {
        const text = (commentTexts[vlogId] || "").trim();
        if (!text) return;

        try {
            await fetch(`${API_URL}/vlogs/${vlogId}/comment`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userEmail: user.email, userName: user.name, text }),
            });
            setCommentTexts(prev => ({ ...prev, [vlogId]: "" }));
            toggleComments(vlogId); // refresh comments
        } catch (err) {
            console.error("Error syncing comment", err);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
        fetchVlogs(search, 0);
    };
    function formatRelativeTime(dateString) {
        const d = new Date(dateString);
        const diff = Math.floor((Date.now() - d) / 1000);
        if (diff < 60) return 'Just now';
        if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
        if (diff < 86400) return Math.floor(diff / 3600) + ' hours ago';
        return d.toLocaleDateString();
    }
    async function checkLike(vlogId, email) {
        try {
            const res = await fetch(`${API_URL}/check?vlogId=${vlogId}&email=${email}`);
            if (!res.ok) throw new Error('Failed to check like status');
            const data = await res.json();

            setLikes(prev => ({ ...prev, [vlogId]: data.liked }));
            return data.liked;
        } catch (err) {
            console.error('Error checking like:', err);
            return false;
        }
    }
    // Example: fetch like status for all vlogs after loading them
    useEffect(() => {
        async function fetchLikes() {
            if (!user?.email) return; // safeguard
            const email = user.email;
            for (const vlog of vlogs) {
                await checkLike(vlog._id, email);
            }
        }
        if (vlogs.length > 0) {
            fetchLikes();
        }
    }, [vlogs, user]);
    /*useEffect(() => {
        async function refreshVlogs() {
            const res = await fetch(`${API_URL}/vlogs?page=0&limit=4`);
            if (!res.ok) return;
            const data = await res.json();

            // Replace only if different from cached
            const cached = JSON.parse(localStorage.getItem("vlogs_cache"));
            if (!cached || JSON.stringify(cached.vlogs) !== JSON.stringify(data.vlogs)) {
                setVlogs(data.vlogs);
                localStorage.setItem("vlogs_cache", JSON.stringify({
                    ...cached,
                    vlogs: data.vlogs,
                    page: 0,
                }));
            }
        }

        refreshVlogs(); // run once
        const interval = setInterval(refreshVlogs, 5 * 60 * 1000); // every 5 mins
        return () => clearInterval(interval);
    }, []);
    */
    return (
        <div className="vlogs-main-container">
            <Navbar />
            <div className="vlogs-container">
                <div className="vlogs-top-controls">
                    <button className="create-vlog-btn" onClick={() => setShowModal(true)}>+ Create</button>
                    <form className="vlogs-search-form" onSubmit={handleSearch}>
                        <input type="text" placeholder="Search vlogs..." value={search} onChange={e => setSearch(e.target.value)} />
                        <button type="submit">Search</button>
                    </form>
                </div>

                {message && <div className={`message ${message.type}`}>{message.text}</div>}

                <div className="vlogs-grid">
                    {vlogs.map((vlog, index) => (
                        <div ref={index === vlogs.length - 1 ? lastVlogRef : null} key={vlog._id} className="vlog-card">

                            <div className="vlog-header">

                                <Link to={`/user/${btoa(vlog.userEmail)}?username=${encodeURIComponent(vlog.userName)}`}>
                                    <img
                                        src={profileImages[vlog.userEmail] || "https://www.pngmart.com/files/23/Profile-PNG-Photo.png"}
                                        alt={vlog.userName}
                                        className="vlog-profile-pic"
                                    />
                                </Link>

                                <span className="vlog-username">{vlog.userName}</span>
                                <span className="vlog-posted-time">{formatRelativeTime(vlog.createdAt || vlog.time)}</span>
                                {/*<span style={{ marginLeft: 'auto', color: '#ccc', fontSize: '1.5rem', cursor: 'pointer' }}>...</span>*/}
                            </div>

                            <div className="vlog-text">
                                <h3>{vlog.title}</h3>
                                <p>{vlog.description}</p>

                                <div className="vlog-media-container">
                                    {vlog.imageUrl && (
                                        isImage(vlog.imageUrl)
                                            ? <img src={vlog.imageUrl} alt={vlog.title} className="vlog-media" />
                                            : isVideo(vlog.imageUrl)
                                                ? <video src={vlog.imageUrl} controls className="vlog-media" />
                                                : <p>Unsupported media type</p>
                                    )}
                                </div>

                                <div className="vlog-actions">
                                    <div className="action-group">
                                        <span className="action-icon" onClick={() => toggleLike(vlog._id)} title="Like">
                                            {likes[vlog._id] ? (
                                                <svg
                                                    width="28"
                                                    height="28"
                                                    viewBox="0 0 24 24"
                                                    fill="#ff4747"  // for filled heart
                                                    stroke="#ff4747"
                                                    strokeWidth="2"
                                                >
                                                    <path d="M12 21s-6.7-5.4-8.2-7C2 12.1 1.6 8.7 4.2 6.7 6.1 5.2 9 6.8 12 10c3-3.2 5.9-4.8 7.8-3.3 2.6 2 .2 5.4-.8 7.3C18.7 15.6 12 21 12 21z" />
                                                </svg>

                                            ) : (
                                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M12 21s-6.7-5.4-8.2-7C2 12.1 1.6 8.7 4.2 6.7 6.1 5.2 9 6.8 12 10c3-3.2 5.9-4.8 7.8-3.3 2.6 2 .2 5.4-.8 7.3C18.7 15.6 12 21 12 21z" />
                                                </svg>
                                            )}
                                        </span>

                                        <div className="action-label">{vlog.likeCount || 0} likes</div>
                                    </div>
                                    <div className="action-group">
                                        <span className="action-icon" onClick={() => toggleComments(vlog._id)}>
                                            <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                                <path d="M21 15.5a2.5 2.5 0 0 1-2.5 2.5H7l-4 4V5.5A2.5 2.5 0 0 1 5.5 3h13A2.5 2.5 0 0 1 21 5.5z" />
                                            </svg>
                                        </span>
                                        <div className="action-label">
                                            {(vlog.comments?.length || 0)} comments
                                        </div>
                                    </div>

                                    {/*<div className="action-group" style={{ marginLeft: 'auto' }}>
                                        <span className="action-icon">
                                            <svg width="28" height="28" fill="#fff" viewBox="0 0 24 24">
                                                <circle cx="5" cy="12" r="2" />
                                                <circle cx="12" cy="12" r="2" />
                                                <circle cx="19" cy="12" r="2" />
                                            </svg>
                                        </span>
                                    </div>*/}
                                </div>


                                {openComments[vlog._id] && (
                                    <div className="vlog-comments active">
                                        {(vlog.comments || []).map(c => (
                                            <p key={c._id}><strong>{c.userName}:</strong> {c.text}</p>
                                        ))}
                                        <input
                                            type="text"
                                            placeholder="Add a comment..."
                                            value={commentTexts[vlog._id] || ""}
                                            onChange={e => handleCommentChange(vlog._id, e.target.value)}
                                        />
                                        <button onClick={() => handleAddComment(vlog._id)}>Post</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {loading && <div className="loading-destinations">
                        <div className="loading-spinner"></div>
                    </div>}
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Create a Vlog</h2>
                        <form onSubmit={handleCreateVlog} className="create-vlog-form">
                            <input type="text" placeholder="Title *" value={title} onChange={e => setTitle(e.target.value)} required />
                            <textarea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
                            <input type="text" placeholder="Tags" value={tags} onChange={e => setTags(e.target.value)} />
                            <input type="file" accept="video/*,image/*" onChange={e => setFile(e.target.files[0])} required />
                            <div className="modal-buttons">
                                <button type="submit" disabled={uploading}>{uploading ? "Uploading..." : "Upload"}</button>
                                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
